import { Link, NavLink, Outlet } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDownToLine,
  ArrowUpFromLine,
  ArrowRight,
  CalendarRange,
  LineChart,
  Megaphone,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { t } from '@/locales/i18n';
import { usePermission } from '@/hooks/usePermission';
import { AccessDenied } from '@/components/AccessDenied';

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

const ITEMS: NavItem[] = [
  { to: '/echeances/a-encaisser', label: t('echeances.nav.aEncaisser'), icon: ArrowDownToLine },
  { to: '/echeances/a-payer', label: t('echeances.nav.aPayer'), icon: ArrowUpFromLine },
  { to: '/echeances/relances', label: t('echeances.nav.relances'), icon: Megaphone },
  { to: '/echeances/plans', label: t('echeances.nav.plans'), icon: CalendarRange },
  { to: '/echeances/cashflow', label: t('echeances.nav.cashflow'), icon: LineChart },
];

export function EcheancesLayout(): JSX.Element {
  const canView = usePermission('echeanciers.view');
  if (!canView) return <AccessDenied feature="echeanciers.view" />;
  return (
    <div className="flex h-full flex-col">
      {/* Sprint 9.1 — Module legacy depuis Sprint 6.9.11 :
          le CRM nourrit recouvrement_entreprises (et non plus echeances).
          Les pages ici restent utilisables pour les saisies historiques /
          plans de paiement, mais le workflow B2B principal est ailleurs. */}
      <div className="flex items-start gap-2 border-b border-amber-200 bg-amber-50 px-4 py-2 text-xs text-amber-900">
        <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span className="flex-1">
          <strong>Module legacy.</strong> Pour les nouvelles factures B2B, utilisez{' '}
          <Link
            to="/finances/consolides/recouvrement"
            className="inline-flex items-center gap-0.5 font-semibold text-amber-900 underline hover:text-amber-700"
          >
            Recouvrement Entreprises <ArrowRight className="h-3 w-3" />
          </Link>{' '}
          (cohérent avec le widget Trésorerie Dashboard). Les saisies manuelles, plans de paiement
          et relances restent gérables ici pour les flux non-CRM.
        </span>
      </div>

      <div className="border-b border-border bg-card">
        <div className="flex items-end gap-1 overflow-x-auto px-4">
          {ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 whitespace-nowrap border-b-2 px-3 py-3 text-sm transition-colors',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:border-border hover:text-foreground',
                )
              }
            >
              <item.icon className="h-4 w-4" />
              {item.label}
            </NavLink>
          ))}
        </div>
      </div>
      <div className="scrollbar-thin flex-1 overflow-y-auto bg-secondary/20">
        <Outlet />
      </div>
    </div>
  );
}
